// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { featureIsEnabled } from '@ta/common.features';
import { ShareCta } from '@ta/public.share-cta';
import {
  BoostAction,
  DisabledSocialAction,
  LikeAction,
  NestedSocialAction,
  SocialActionBar,
} from '@ta/social.actions';
import type { AuthZInfo } from '@ta/common.authz';

import type { ForumPostSectionProps } from '@ta/social.section-forum-post';
import ForumPostSection, { getForumPostUrl } from '@ta/social.section-forum-post';
import SocialStatisticsBar from '@ta/social.statistics-bar';
import { getDisplayName } from '@ta/social.member';

type ExternalProps = {|
  // Take everything from ForumPostSectionProps except socialActionBar and socialStatisticsBar
  ...$Diff<
    ForumPostSectionProps,
    {| socialActionBar?: React.Node, socialStatisticsBar?: React.Node |},
  >,
  loggedInMemberAuthorization?: AuthZInfo,
|};

type Props = {|
  ...ExternalProps,
  language: string,
|};

const ForumPostSectionWrap = (props: Props) => {
  const { loggedInMemberAuthorization, ...copiedProps } = props;
  const { post, member, actor, language } = copiedProps;
  // If the language of the forum post doesn't match, use absolute url instead. Forum posts are on a per-language basis
  const postUrl = getForumPostUrl(post.object, language);
  const isTopic = post.object.id === post.object.parentId;
  if (!post.object) {
    return null;
  }

  const boostAction =
    featureIsEnabled('social_boosting_enabled') &&
    (loggedInMemberAuthorization && loggedInMemberAuthorization.canBoostContent) ? (
      <BoostAction
        socialObjectRef={{ type: 'FORUM_POST', id: post.object.id }}
        socialStatistics={post.object.socialStatistics}
        typename={post.object.__typename}
      />
    ) : null;

  const repostProps = {
    modalName: `repost_${post.object.__typename}_${post.object.id}`,
    repostPreviewProps: { feedSectionItem: post, member, actor },
    actor,
    socialStatistics: post.object.socialStatistics,
  };

  const socialActionBar = (
    <SocialActionBar>
      {/* TRVX-13661: Likes -> Helpful Tech Debt */}
      <LikeAction
        item={post.object}
        socialObject={{ objectType: 'FORUM_POST', objectId: post.object.id }}
      />
      <NestedSocialAction>
        {/* Share: TRVX-9358 */}
        {featureIsEnabled('social_ugc_share') ? (
          <ShareCta
            url={postUrl}
            itemType={isTopic ? 'ForumPost' : 'ForumResponse'}
            itemId={post.object.id}
            styleAsSocialAction
            alignEdge="right"
            repostProps={repostProps}
          />
        ) : (
          <DisabledSocialAction actionType="Share" />
        )}
        {boostAction}
      </NestedSocialAction>
    </SocialActionBar>
  );

  const socialStatisticsBar = (
    <SocialStatisticsBar
      stats={post.object.socialStatistics}
      socialRef={{
        type: post.object.__typename,
        id: `${post.object.id}`,
      }}
      socialProofParams={{ displayName: getDisplayName(props.actor), isForumTopic: isTopic }}
      deduplicate
    />
  );

  return (
    <ForumPostSection
      {...copiedProps}
      socialActionBar={socialActionBar}
      socialStatisticsBar={socialStatisticsBar}
    />
  );
};

ForumPostSection.defaultProps = {
  isSingleObject: false,
  isEmbedded: false,
  member: null,
  actor: null,
  deleteObject: null,
  loggedInMemberAuthorization: undefined,
  clusterId: undefined,
};

export default connect<Props, ExternalProps, _, _, _, _>((state, props: ExternalProps) => ({
  ...props,
  language: state.i18n.language,
}))(ForumPostSectionWrap);
